/**
 * FlowCanvas Component
 *
 * Visual canvas for flow building (simplified version).
 * Displays flow elements and connections.
 *
 * @component
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Play,
  Monitor,
  GitBranch,
  Equal,
  RefreshCw,
  Download,
  PlusCircle,
  Edit2,
  Trash2,
  Mail,
  MessageSquare,
  Clock,
  StopCircle,
  Zap,
  Database,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Move
} from 'lucide-react';
import { StandardButton, StandardCard } from '../../standard';
import type { FlowElement, ElementType } from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface FlowCanvasProps {
  elements: FlowElement[];
  readOnly?: boolean;
  onElementClick?: (element: FlowElement) => void;
  onElementDelete?: (elementId: string) => void;
  onAddElement?: (type: ElementType, afterElementId?: string) => void;
  selectedElementId?: string;
}

interface CanvasNode {
  element: FlowElement;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Connection {
  id: string;
  from: string;
  to: string;
  label?: string;
  isDefault?: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;
const NODE_SPACING_X = 60;
const NODE_SPACING_Y = 100;
const START_X = 50;
const START_Y = 50;

// ============================================
// ELEMENT ICON COMPONENT
// ============================================

const getElementIcon = (type: ElementType, size: number = 18) => {
  switch (type) {
    case 'start':
      return <Play size={size} className="text-green-500" />;
    case 'screen':
      return <Monitor size={size} className="text-blue-500" />;
    case 'decision':
      return <GitBranch size={size} className="text-yellow-500" />;
    case 'assignment':
      return <Equal size={size} className="text-purple-500" />;
    case 'loop':
      return <RefreshCw size={size} className="text-orange-500" />;
    case 'get_records':
      return <Download size={size} className="text-cyan-500" />;
    case 'create_records':
      return <PlusCircle size={size} className="text-green-500" />;
    case 'update_records':
      return <Edit2 size={size} className="text-blue-500" />;
    case 'delete_records':
      return <Trash2 size={size} className="text-red-500" />;
    case 'email_alert':
      return <Mail size={size} className="text-pink-500" />;
    case 'post_to_feed':
      return <MessageSquare size={size} className="text-indigo-500" />;
    case 'wait':
      return <Clock size={size} className="text-gray-500" />;
    case 'action':
      return <Zap size={size} className="text-yellow-500" />;
    case 'subflow':
      return <Database size={size} className="text-teal-500" />;
    case 'end':
      return <StopCircle size={size} className="text-red-500" />;
    default:
      return <Zap size={size} className="text-gray-500" />;
  }
};

const getElementColor = (type: ElementType): string => {
  switch (type) {
    case 'start':
      return 'border-green-500 bg-green-50 dark:bg-green-900/20';
    case 'screen':
      return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
    case 'decision':
      return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    case 'assignment':
      return 'border-purple-500 bg-purple-50 dark:bg-purple-900/20';
    case 'loop':
      return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
    case 'get_records':
      return 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20';
    case 'create_records':
      return 'border-green-500 bg-green-50 dark:bg-green-900/20';
    case 'update_records':
      return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
    case 'delete_records':
      return 'border-red-500 bg-red-50 dark:bg-red-900/20';
    case 'email_alert':
      return 'border-pink-500 bg-pink-50 dark:bg-pink-900/20';
    case 'post_to_feed':
      return 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20';
    case 'wait':
      return 'border-gray-500 bg-gray-50 dark:bg-gray-700/50';
    case 'action':
      return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    case 'subflow':
      return 'border-teal-500 bg-teal-50 dark:bg-teal-900/20';
    case 'end':
      return 'border-red-500 bg-red-50 dark:bg-red-900/20';
    default:
      return 'border-gray-300 bg-gray-50 dark:bg-gray-700/50';
  }
};

// ============================================
// CANVAS NODE COMPONENT
// ============================================

interface CanvasNodeProps {
  node: CanvasNode;
  isSelected: boolean;
  readOnly: boolean;
  onClick: () => void;
  onDelete?: () => void;
}

const CanvasNodeComponent: React.FC<CanvasNodeProps> = ({
  node,
  isSelected,
  readOnly,
  onClick,
  onDelete
}) => {
  const { element, x, y, width, height } = node;

  // Special styling for start and end nodes
  const isTerminal = element.type === 'start' || element.type === 'end';
  const isDecision = element.type === 'decision';

  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={onClick}
      style={{ cursor: readOnly ? 'pointer' : 'move' }}
    >
      {/* Node shape */}
      {isTerminal ? (
        <circle
          cx={width / 2}
          cy={height / 2}
          r={25}
          className={`stroke-2 ${
            element.type === 'start'
              ? 'fill-green-100 stroke-green-500 dark:fill-green-900/30'
              : 'fill-red-100 stroke-red-500 dark:fill-red-900/30'
          } ${isSelected ? 'stroke-[3px]' : ''}`}
        />
      ) : isDecision ? (
        <polygon
          points={`${width / 2},0 ${width},${height / 2} ${width / 2},${height} 0,${height / 2}`}
          className={`fill-yellow-100 stroke-yellow-500 stroke-2 dark:fill-yellow-900/30 ${
            isSelected ? 'stroke-[3px]' : ''
          }`}
        />
      ) : (
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          rx={8}
          className={`stroke-2 ${getElementColor(element.type)} ${
            isSelected ? 'stroke-blue-600 stroke-[3px]' : ''
          }`}
          style={{
            fill: 'currentColor',
            fillOpacity: 0.1
          }}
        />
      )}

      {/* Icon */}
      <foreignObject
        x={isTerminal ? (width / 2 - 12) : 12}
        y={isTerminal ? (height / 2 - 12) : (height / 2 - 12)}
        width={24}
        height={24}
      >
        <div className="flex items-center justify-center w-full h-full">
          {getElementIcon(element.type, isTerminal ? 20 : 18)}
        </div>
      </foreignObject>

      {/* Label - not for terminal nodes */}
      {!isTerminal && (
        <foreignObject
          x={40}
          y={8}
          width={width - 52}
          height={height - 16}
        >
          <div className="w-full h-full flex flex-col justify-center">
            <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
              {element.label}
            </p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">
              {element.type.replace(/_/g, ' ')}
            </p>
          </div>
        </foreignObject>
      )}

      {/* Delete button - only in edit mode */}
      {!readOnly && !isTerminal && isSelected && onDelete && (
        <foreignObject x={width - 20} y={-8} width={24} height={24}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
          >
            <Trash2 size={12} />
          </button>
        </foreignObject>
      )}
    </g>
  );
};

// ============================================
// CONNECTION LINE COMPONENT
// ============================================

interface ConnectionLineProps {
  connection: Connection;
  nodes: Map<string, CanvasNode>;
}

const ConnectionLine: React.FC<ConnectionLineProps> = ({ connection, nodes }) => {
  const fromNode = nodes.get(connection.from);
  const toNode = nodes.get(connection.to);

  if (!fromNode || !toNode) return null;

  const isTerminalFrom = fromNode.element.type === 'start' || fromNode.element.type === 'end';
  const isDecisionFrom = fromNode.element.type === 'decision';
  const isTerminalTo = toNode.element.type === 'start' || toNode.element.type === 'end';

  // Calculate connection points
  let startX = fromNode.x + fromNode.width / 2;
  let startY = fromNode.y + fromNode.height;

  if (isTerminalFrom) {
    startY = fromNode.y + fromNode.height / 2 + 25;
  } else if (isDecisionFrom) {
    // Connect from bottom of diamond
    startY = fromNode.y + fromNode.height;
  }

  let endX = toNode.x + toNode.width / 2;
  let endY = toNode.y;

  if (isTerminalTo) {
    endY = toNode.y + toNode.height / 2 - 25;
  }

  // Calculate control points for curved line
  const midY = (startY + endY) / 2;

  // Path with curved line
  const path = `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;

  return (
    <g>
      {/* Connection line */}
      <path
        d={path}
        fill="none"
        className="stroke-gray-400 dark:stroke-gray-500"
        strokeWidth={2}
        markerEnd="url(#arrowhead)"
      />

      {/* Label */}
      {connection.label && (
        <text
          x={(startX + endX) / 2}
          y={(startY + endY) / 2 - 8}
          className="text-xs fill-gray-600 dark:fill-gray-400"
          textAnchor="middle"
        >
          {connection.label}
        </text>
      )}
    </g>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  elements,
  readOnly = false,
  onElementClick,
  onElementDelete,
  onAddElement,
  selectedElementId
}) => {
  const canvasRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Calculate node positions using auto-layout
  const { nodes, connections, canvasSize } = useMemo(() => {
    const nodeMap = new Map<string, CanvasNode>();
    const connectionList: Connection[] = [];

    if (elements.length === 0) {
      return { nodes: nodeMap, connections: connectionList, canvasSize: { width: 400, height: 300 } };
    }

    // Find start element
    const startElement = elements.find(e => e.type === 'start');
    if (!startElement) {
      // Fallback: just layout elements linearly
      elements.forEach((element, index) => {
        nodeMap.set(element.id, {
          element,
          x: START_X,
          y: START_Y + index * (NODE_HEIGHT + NODE_SPACING_Y),
          width: NODE_WIDTH,
          height: NODE_HEIGHT
        });
      });
      return {
        nodes: nodeMap,
        connections: connectionList,
        canvasSize: {
          width: NODE_WIDTH + START_X * 2,
          height: elements.length * (NODE_HEIGHT + NODE_SPACING_Y) + START_Y
        }
      };
    }

    // BFS to layout elements from start
    const visited = new Set<string>();
    const queue: Array<{ elementId: string; level: number; branch: number }> = [];
    const levelCounts: Map<number, number> = new Map();

    queue.push({ elementId: startElement.id, level: 0, branch: 0 });

    while (queue.length > 0) {
      const { elementId, level, branch } = queue.shift()!;

      if (visited.has(elementId)) continue;
      visited.add(elementId);

      const element = elements.find(e => e.id === elementId);
      if (!element) continue;

      // Track elements per level
      const currentCount = levelCounts.get(level) || 0;
      levelCounts.set(level, currentCount + 1);

      // Calculate position
      const x = START_X + branch * (NODE_WIDTH + NODE_SPACING_X);
      const y = START_Y + level * (NODE_HEIGHT + NODE_SPACING_Y);

      nodeMap.set(elementId, {
        element,
        x,
        y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT
      });

      // Process connectors
      element.connectors.forEach((connector, index) => {
        connectionList.push({
          id: connector.id,
          from: elementId,
          to: connector.targetElementId,
          label: connector.label,
          isDefault: connector.isDefault
        });

        if (!visited.has(connector.targetElementId)) {
          queue.push({
            elementId: connector.targetElementId,
            level: level + 1,
            branch: element.type === 'decision' ? index : branch
          });
        }
      });
    }

    // Add any unvisited elements
    elements.forEach((element, index) => {
      if (!visited.has(element.id)) {
        nodeMap.set(element.id, {
          element,
          x: START_X + (NODE_WIDTH + NODE_SPACING_X) * 2,
          y: START_Y + index * (NODE_HEIGHT + NODE_SPACING_Y),
          width: NODE_WIDTH,
          height: NODE_HEIGHT
        });
      }
    });

    // Calculate canvas size
    let maxX = 0;
    let maxY = 0;
    nodeMap.forEach(node => {
      maxX = Math.max(maxX, node.x + node.width);
      maxY = Math.max(maxY, node.y + node.height);
    });

    return {
      nodes: nodeMap,
      connections: connectionList,
      canvasSize: {
        width: Math.max(maxX + START_X * 2, 400),
        height: Math.max(maxY + START_Y * 2, 300)
      }
    };
  }, [elements]);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Zoom handlers
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.1, 0.5));
  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.max(0.5, Math.min(2, z + delta)));
    }
  };

  if (elements.length === 0) {
    return (
      <StandardCard className="p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
            <GitBranch size={32} className="text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Elements Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This flow doesn't have any elements. Add elements to build your flow.
            </p>
            {!readOnly && onAddElement && (
              <StandardButton
                variant="primary"
                onClick={() => onAddElement('screen')}
                leftIcon={<PlusCircle size={16} />}
              >
                Add First Element
              </StandardButton>
            )}
          </div>
        </div>
      </StandardCard>
    );
  }

  return (
    <StandardCard className="relative overflow-hidden">
      {/* Toolbar */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-1">
        <StandardButton variant="ghost" size="sm" onClick={handleZoomOut} title="Zoom Out">
          <ZoomOut size={16} />
        </StandardButton>
        <span className="text-xs text-gray-600 dark:text-gray-400 min-w-[40px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <StandardButton variant="ghost" size="sm" onClick={handleZoomIn} title="Zoom In">
          <ZoomIn size={16} />
        </StandardButton>
        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
        <StandardButton variant="ghost" size="sm" onClick={handleResetView} title="Reset View">
          <Maximize2 size={16} />
        </StandardButton>
      </div>

      {/* Pan indicator */}
      {isPanning && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-blue-500 text-white rounded-lg px-3 py-1 text-sm">
          <Move size={14} />
          Panning
        </div>
      )}

      {/* Canvas */}
      <div
        className="w-full h-[500px] overflow-hidden cursor-grab"
        style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <svg
          ref={canvasRef}
          width="100%"
          height="100%"
          viewBox={`${-pan.x / zoom} ${-pan.y / zoom} ${canvasSize.width / zoom} ${canvasSize.height / zoom}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Defs for arrow markers */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                className="fill-gray-400 dark:fill-gray-500"
              />
            </marker>
          </defs>

          {/* Grid pattern */}
          <defs>
            <pattern
              id="grid"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                className="stroke-gray-200 dark:stroke-gray-700"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Connections */}
          <g>
            {connections.map(connection => (
              <ConnectionLine
                key={connection.id}
                connection={connection}
                nodes={nodes}
              />
            ))}
          </g>

          {/* Nodes */}
          <g>
            {Array.from(nodes.values()).map(node => (
              <CanvasNodeComponent
                key={node.element.id}
                node={node}
                isSelected={selectedElementId === node.element.id}
                readOnly={readOnly}
                onClick={() => onElementClick?.(node.element)}
                onDelete={() => onElementDelete?.(node.element.id)}
              />
            ))}
          </g>
        </svg>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Start</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <span>Screen</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rotate-45 bg-yellow-500" />
            <span>Decision</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-purple-500" />
            <span>Assignment</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-cyan-500" />
            <span>Data</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>End</span>
          </div>
        </div>
      </div>
    </StandardCard>
  );
};

export default FlowCanvas;
